import React, { useEffect, useState } from 'react';
import NavigationStepper, {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../Components/Stepper/NavigationStepper';
import { PrescriptionType } from '../../../Enums/PrescriptionType.enum';
import ScrollContainer from '../../Base/Components/ScrollContainer';
import { FormValues } from '../../MagicForm/Interfaces/FormValues';
import EPrescriptionPrescriptionInformation, {
	EPrescriptionIssuePrescriptionFormData,
} from './EPrescriptionIssuePrescription';
import { EPrescriptionHVLPMedicament } from './EPrescriptionMedicamentsHVLPForm';
import { EPrescriptionIPLPMedicament } from './EPrescriptionMedicamentsIPLPForm';
import EPrescriptionMedicamentsWrapper, {
	EPrescriptionMedicament,
} from './EPrescriptionMedicamentsWrapper';
import EPrescriptionPatientInformation, {
	EPrescriptionPatientInformationFormData,
} from './EPrescriptionPatientInformation';
import SearchMedicamentRecordAuthorization, {
	SearchMedicamentRecordAuthorizationSource,
} from '../../../Components/eRecept/SearchMedicamentRecord/SearchMedicamentRecordAuthorization';
import { useEffectAsync } from '../../../helpers';
import { checkSUKLCredentialsAvailability } from '../Services/SUKLCredentials.service';

interface EPrescriptionWrapperProps {
	patientId: string;
	onPrescriptionCreated: (receiptId?: string) => void;
	defaultValues?: any;
	setIsLoading: (isLoading: boolean) => void;
	customOnSubmit?: (values: any) => void;
}

interface MedicamentsValues {
	hvlp?: EPrescriptionHVLPMedicament[];
	iplp?: EPrescriptionIPLPMedicament[];
}

export interface Values {
	patientInformation?: EPrescriptionPatientInformationFormData;
	medicaments?: MedicamentsValues;
	issuePrescription?: EPrescriptionIssuePrescriptionFormData;

	[key: string]: any;
}

const EPrescriptionWrapper: React.FC<EPrescriptionWrapperProps> = ({
	patientId,
	onPrescriptionCreated,
	defaultValues,
	setIsLoading,
	customOnSubmit,
}) => {
	const [formValues, setFormValues] = useState<Values>({});
	const [steps, setSteps] = useState<NavigationStepperItemType[]>([]);
	const [initialStep, setInitialStep] = useState('informationAboutPatient');

	useEffect(() => {
		if (defaultValues?.formData?.default?.medicaments) {
			setFormValues({
				medicaments: defaultValues.formData.default.medicaments,
			});
		}
	}, [defaultValues]);

	const handlePatientInformation = (
		params: NavigationStepperComponentParams,
		values: FormValues,
	) => {
		setFormValues(prevState => {
			return {
				...prevState,
				patientInformation: values,
				issuePrescription: {
					...(prevState?.issuePrescription || {}),
					notificationPhoneNumber: `${values.phoneNumber}`,
					notificationEmail: `${values.electronicMail}`,
				},
			};
		});
		params.setActiveStepPath('medicaments');
	};

	const handleMedicaments = (values: EPrescriptionMedicament[]) => {
		setFormValues(prevState => {
			return {
				...prevState,
				medicaments: {
					hvlp: (values as unknown as EPrescriptionHVLPMedicament[])?.filter(
						({ formType }) => formType === PrescriptionType.HVLP,
					),
					iplp: (values as unknown as EPrescriptionIPLPMedicament[])?.filter(
						({ formType }) => formType === PrescriptionType.IPLP,
					),
				},
			};
		});
	};

	const handleAfterMedicaments = (params: NavigationStepperComponentParams) => {
		params.setActiveStepPath('issuePrescription');
	};

	useEffectAsync(async () => {
		setIsLoading(true);
		const tempSteps: NavigationStepperItemType[] = [];

		const isMissingSUKLCredentials = await checkSUKLCredentialsAvailability();

		if (isMissingSUKLCredentials) {
			setInitialStep('authorization');
			tempSteps.push({
				name: 'base.verifyOfAccess',
				key: 'authorization',
				component: (params: NavigationStepperComponentParams) => (
					<SearchMedicamentRecordAuthorization
						setIsLoading={params.setIsLoading}
						source={SearchMedicamentRecordAuthorizationSource.ERECEPT}
						onSuccess={() =>
							params.setActiveStepPath('informationAboutPatient')
						}
					/>
				),
			});
		}

		tempSteps.push(
			{
				name: 'base.patientDetails',
				key: 'informationAboutPatient',
				component: (params: NavigationStepperComponentParams) => (
					<EPrescriptionPatientInformation
						patientId={patientId}
						onSuccess={values => handlePatientInformation(params, values)}
						formValues={formValues.patientInformation}
					/>
				),
			},
			{
				name: 'base.inputMeds',
				key: 'medicaments',
				component: (params: NavigationStepperComponentParams) => (
					<EPrescriptionMedicamentsWrapper
						patientId={patientId}
						onMedicamentsUpdate={values => handleMedicaments(values)}
						onSuccess={() => handleAfterMedicaments(params)}
						defaultValues={formValues.medicaments}
						formValues={formValues}
					/>
				),
			},
			{
				name: 'base.issuePrescription',
				key: 'issuePrescription',
				component: (params: NavigationStepperComponentParams) => (
					<EPrescriptionPrescriptionInformation
						patientId={patientId}
						formValues={formValues}
						defaultValues={formValues.issuePrescription}
						onLoading={(isLoading: boolean) => params.setIsLoading(isLoading)}
						onPrescriptionCreated={onPrescriptionCreated}
						customOnSubmit={customOnSubmit}
					/>
				),
			},
		);

		setSteps(tempSteps);
		setIsLoading(false);
	}, [formValues]);

	return (
		<ScrollContainer>
			<NavigationStepper
				steps={steps}
				initialStepPath={initialStep}
				canSkip={false}
			/>
		</ScrollContainer>
	);
};

export default EPrescriptionWrapper;
